import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase, API_URL } from '../utils/constants';

const AuthModal = ({ onClose, onAuth, initialMode = 'login' }) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState(initialMode); // 'login', 'signup', 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [emailOptIn, setEmailOptIn] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (mode === 'signup') {
        const { data: signUpData, error } = await supabase.auth.signUp({ email, password });
        if (error) throw error;
        // Send email opt-in preference if checked
        if (emailOptIn && signUpData?.session?.access_token) {
          fetch(`${API_URL}/api/update-email-opt-in`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${signUpData.session.access_token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ emailOptIn: true })
          }).catch(() => {});
        }
        setSuccess(t('auth.checkEmail'));
      } else if (mode === 'forgot') {
        const { error } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: 'https://www.contenttransform.com/reset-password'
        });
        if (error) throw error;
        setSuccess(t('auth.resetEmailSent'));
      } else {
        const { data, error } = await supabase.auth.signInWithPassword({ email, password });
        if (error) throw error;
        onAuth(data.session);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: window.location.origin
        }
      });
      if (error) throw error;
    } catch (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, backdropFilter: 'blur(8px)', animation: 'fadeIn 0.2s ease' }} onClick={onClose}>
      <div style={{ background: 'var(--bg-card)', borderRadius: '20px', padding: '40px', width: '100%', maxWidth: '420px', position: 'relative', boxShadow: 'var(--shadow-lg)', border: '1px solid var(--border-color)', animation: 'modalIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }} onClick={e => e.stopPropagation()}>
        <button style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '24px', cursor: 'pointer', transition: 'color 0.2s' }} onClick={onClose}>×</button>

        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ fontSize: '40px', marginBottom: '12px' }}>{mode === 'forgot' ? '🔐' : '👋'}</div>
          <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
            {mode === 'login' ? t('auth.welcomeBack') : mode === 'signup' ? t('auth.createAccount') : t('auth.resetPassword')}
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            {mode === 'login' ? t('auth.signInToContinue') : mode === 'signup' ? t('auth.startTransforming') : t('auth.sendResetLink')}
          </p>
        </div>

        {mode !== 'forgot' && (
          <>
            <button
              onClick={handleGoogleLogin}
              disabled={googleLoading}
              style={{
                width: '100%', padding: '14px', borderRadius: '10px',
                border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '15px', fontWeight: '500',
                cursor: 'pointer', fontFamily: 'inherit', display: 'flex',
                alignItems: 'center', justifyContent: 'center', gap: '10px',
                transition: 'all 0.2s', opacity: googleLoading ? 0.7 : 1
              }}
              onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent-primary)'}
              onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border-color)'}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853"/>
                <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.997 8.997 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05"/>
                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 6.29C4.672 4.163 6.656 2.58 9 3.58z" fill="#EA4335"/>
              </svg>
              {googleLoading ? t('auth.pleaseWait') : t('auth.continueWithGoogle')}
            </button>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', margin: '20px 0' }}>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
              <span style={{ fontSize: '13px', color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{t('auth.orContinueWith')}</span>
              <div style={{ flex: 1, height: '1px', background: 'var(--border-color)' }} />
            </div>
          </>
        )}

        <form onSubmit={handleSubmit}>
          <input
            type="email"
            placeholder={t('auth.emailPlaceholder')}
            value={email}
            onChange={e => setEmail(e.target.value)}
            style={{
              width: '100%', padding: '14px 16px', borderRadius: '10px',
              border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
              color: 'var(--text-primary)', fontSize: '15px', marginBottom: '12px',
              outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
              transition: 'border-color 0.2s'
            }}
            onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
            onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
            required
          />

          {mode !== 'forgot' && (
            <input
              type="password"
              placeholder={t('auth.passwordPlaceholder')}
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={{
                width: '100%', padding: '14px 16px', borderRadius: '10px',
                border: '2px solid var(--border-color)', background: 'var(--bg-secondary)',
                color: 'var(--text-primary)', fontSize: '15px', marginBottom: '16px',
                outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
                transition: 'border-color 0.2s'
              }}
              onFocus={e => e.target.style.borderColor = 'var(--accent-primary)'}
              onBlur={e => e.target.style.borderColor = 'var(--border-color)'}
              required
              minLength={8}
            />
          )}

          {mode === 'signup' && (
            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px', cursor: 'pointer', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <input
                type="checkbox"
                checked={emailOptIn}
                onChange={e => setEmailOptIn(e.target.checked)}
                style={{ width: '16px', height: '16px', accentColor: 'var(--accent-primary)', cursor: 'pointer' }}
              />
              Send me tips on growing with repurposed content
            </label>
          )}

          {error && <p style={{ color: 'var(--error)', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{error}</p>}
          {success && <p style={{ color: 'var(--success)', fontSize: '13px', marginBottom: '12px', textAlign: 'center' }}>{success}</p>}

          <button
            type="submit"
            style={{
              width: '100%', padding: '14px', borderRadius: '10px', border: 'none',
              background: 'var(--accent-gradient)', color: 'white',
              fontSize: '15px', fontWeight: '600', cursor: 'pointer', fontFamily: 'inherit',
              opacity: loading ? 0.7 : 1, transition: 'transform 0.2s, opacity 0.2s'
            }}
            disabled={loading}
          >
            {loading ? t('auth.pleaseWait') : mode === 'login' ? t('auth.signIn') : mode === 'signup' ? t('auth.createAccount') : t('auth.sendResetLinkBtn')}
          </button>
        </form>

        {mode === 'login' && (
          <button
            style={{
              display: 'block', width: '100%', marginTop: '16px', padding: '10px',
              background: 'none', border: 'none', color: 'var(--accent-primary)',
              fontSize: '14px', cursor: 'pointer', fontFamily: 'inherit'
            }}
            onClick={() => { setMode('forgot'); setError(''); setSuccess(''); }}
          >
            {t('auth.forgotPassword')}
          </button>
        )}

        <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '14px', color: 'var(--text-secondary)' }}>
          {mode === 'login' ? t('auth.noAccount') + ' ' : mode === 'signup' ? t('auth.haveAccount') + ' ' : t('auth.rememberPassword') + ' '}
          <button
            style={{ background: 'none', border: 'none', color: 'var(--accent-primary)', cursor: 'pointer', fontFamily: 'inherit', fontSize: '14px', fontWeight: '600' }}
            onClick={() => { setMode(mode === 'signup' ? 'login' : mode === 'forgot' ? 'login' : 'signup'); setError(''); setSuccess(''); }}
          >
            {mode === 'login' ? t('auth.signUp') : t('auth.signIn')}
          </button>
        </p>
      </div>
    </div>
  );
};

export default AuthModal;
